<template>
  <div class="home-v2">

    <!-- ============ HEADER + FILTRO + KPIS ============ -->
    <div class="dash-header">
      <div class="dash-header-top">
        <div>
          <div class="dash-eyebrow">SALES MAP · LIVE</div>
          <h2 class="dash-title">Pedidos por país <span class="dash-title-tag">{{ rangoLabel }}</span></h2>
        </div>

        <div class="date-range">
          <div class="date-field">
            <label class="date-label">DESDE</label>
            <flat-pickr v-model="resumenFechaDesde" class="date-input" />
          </div>
          <div class="date-field">
            <label class="date-label">HASTA</label>
            <flat-pickr v-model="resumenFechaHasta" class="date-input" />
          </div>
          <button class="btn-filter" @click="cargarResumen" :disabled="cargandoResumen" aria-label="Filtrar por rango de fechas">
            <b-spinner v-if="cargandoResumen" small />
            <feather-icon v-else icon="FilterIcon" size="14" />
            <span>{{ cargandoResumen ? 'Cargando...' : 'Filtrar' }}</span>
          </button>
          <button class="btn-filter" @click="descargarCatalogo('simple')" :disabled="descargandoCatalogo" aria-label="Descargar catálogo de Shopify en Excel">
            <b-spinner v-if="descargandoCatalogo" small />
            <feather-icon v-else icon="DownloadIcon" size="14" />
            <span>{{ descargandoCatalogo ? 'Generando...' : 'Catálogo Excel' }}</span>
          </button>
          <button class="btn-filter" @click="descargarCatalogo('agrupado')" :disabled="descargandoAgrupado" aria-label="Descargar catálogo agrupado por producto">
            <b-spinner v-if="descargandoAgrupado" small />
            <feather-icon v-else icon="LayersIcon" size="14" />
            <span>{{ descargandoAgrupado ? 'Generando...' : 'Catálogo por producto' }}</span>
          </button>
        </div>
      </div>

      <!-- KPI strip -->
      <div class="kpi-strip">
        <div class="kpi-card">
          <div class="kpi-label">PEDIDOS</div>
          <div class="kpi-value">{{ kpiPedidos }}</div>
          <div class="kpi-foot">
            <span class="dot dot-blue"></span>
            <span>en el rango</span>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">UNIDADES</div>
          <div class="kpi-value">{{ kpiUnidades }}</div>
          <div class="kpi-foot">
            <span class="dot dot-orange"></span>
            <span>artículos vendidos</span>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">PAÍSES</div>
          <div class="kpi-value">{{ kpiPaises }}</div>
          <div class="kpi-foot">
            <span class="dot dot-green"></span>
            <span>con envíos</span>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-label">TOP PAÍS</div>
          <div class="kpi-value kpi-value-sm">{{ kpiTopPais.nombre || '—' }}</div>
          <div class="kpi-foot">
            <span>{{ kpiTopPais.count }} pedidos</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ MAPA ============ -->
    <div class="map-card">
      <div class="map-card-header">
        <div class="map-card-title">
          <feather-icon icon="MapPinIcon" size="14" />
          <span>Distribución geográfica</span>
        </div>
        <div class="map-legend">
          <span class="legend-item"><span class="legend-dot legend-dot-current"></span> Últimos 15 días</span>
        </div>
      </div>

      <div v-if="cargando" class="map-loading">
        <div class="map-loading-inner">
          <b-spinner variant="primary" />
          <p>Cargando pedidos en el mapa...</p>
        </div>
      </div>

      <div ref="mapContainer" class="map-container" :class="{ 'is-loading': cargando }"></div>
    </div>

    <!-- ============ RESUMEN PAÍSES ============ -->
    <div class="panel">
      <ResumenPaises :key="resumenKey" :items="resumenOrders" :fechaDesde="resumenFechaDesde" :fechaHasta="resumenFechaHasta" />
    </div>
  </div>
</template>

<script>
import { BCard, BCardText, BLink } from "bootstrap-vue";

import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { AgrupadoViewport, renderer, marcadoresPedidos } from "@/utils/mapaPedidos";

import ResumenPaises from "../layouts/components/home/resumenPaises.vue";
import flatPickr from 'vue-flatpickr-component';
import axios from "@axios";
import moment from "moment";
import { actualizarPermisos } from '@/utils/actualizarPermisos'

export default {
  components: {
    BCard,
    BCardText,
    BLink,
    ResumenPaises,
    flatPickr,
  },
  data() {
    const hoy = new Date();
    const hace15 = new Date();
    hace15.setDate(hoy.getDate() - 15);

    function fmt(d) {
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    }

    return {
      orders: [],
      ordersOlds: [],
      resumenOrders: [],
      map: null,
      markerClusterer: null,
      cargando: true,
      resumenFechaDesde: fmt(hace15),
      resumenFechaHasta: fmt(hoy),
      resumenKey: 0,
      cargandoResumen: false,
      descargandoCatalogo: false,
      descargandoAgrupado: false,
    };
  },
  computed: {
    rangoLabel() {
      if (!this.resumenFechaDesde || !this.resumenFechaHasta) return '';
      const fmt = (s) => {
        const p = s.split('-');
        return p[2] + '/' + p[1];
      };
      return fmt(this.resumenFechaDesde) + ' → ' + fmt(this.resumenFechaHasta);
    },
    kpiPedidos() {
      const pedidosUnicos = new Set();
      (this.resumenOrders || []).forEach(o => {
        if (o && o.id) pedidosUnicos.add(o.id);
        else if (o && o.name) pedidosUnicos.add(o.name);
      });
      return pedidosUnicos.size.toLocaleString('es-ES');
    },
    kpiUnidades() {
      const total = (this.resumenOrders || []).reduce((acc, o) => {
        if (!o || !o.line_items) return acc;
        return acc + o.line_items.reduce((a, li) => a + (Number(li.quantity) || 0), 0);
      }, 0);
      return total.toLocaleString('es-ES');
    },
    kpiPaises() {
      const set = new Set();
      (this.resumenOrders || []).forEach(o => {
        const c = o && o.shipping_address && o.shipping_address.country;
        if (c) set.add(c);
      });
      return set.size;
    },
    kpiTopPais() {
      const map = {};
      (this.resumenOrders || []).forEach(o => {
        const c = o && o.shipping_address && o.shipping_address.country;
        if (!c) return;
        map[c] = (map[c] || 0) + 1;
      });
      let top = { nombre: '', count: 0 };
      Object.keys(map).forEach(k => {
        if (map[k] > top.count) top = { nombre: k, count: map[k] };
      });
      return top;
    },
  },
  async mounted() {
    await window.googleMapsReady;
    this.initMap();
    this.loaditems();
  },
  methods: {
    // Descarga un Excel de catálogo de Shopify.
    //   tipo = 'simple'   -> una fila por variante (con barcode)
    //   tipo = 'agrupado' -> una fila por producto (SKU base + colores)
    async descargarCatalogo(tipo = 'simple') {
      if (this.descargandoCatalogo || this.descargandoAgrupado) return;
      const agrupado = tipo === 'agrupado';
      if (agrupado) { this.descargandoAgrupado = true; } else { this.descargandoCatalogo = true; }
      const ruta = agrupado ? '/escaner/catalogo-agrupado' : '/escaner/catalogo';
      const nombre = agrupado ? 'catalogo_shopify_agrupado.xlsx' : 'catalogo_shopify.xlsx';
      try {
        const { data } = await axios.get(ruta, { responseType: 'blob' });
        const url = URL.createObjectURL(
          new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        );
        const a = document.createElement('a');
        a.href = url;
        a.download = nombre;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('descargarCatalogo:', e);
        this.$bvToast.toast('No se pudo generar el catálogo', {
          title: 'Error', variant: 'danger', solid: true,
        });
      } finally {
        if (agrupado) { this.descargandoAgrupado = false; } else { this.descargandoCatalogo = false; }
      }
    },
    async loaditems() {

      //await actualizarPermisos()

      function formatDate(date) {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }

      const baseEnd = new Date();
      const baseStart = new Date();
      baseStart.setDate(baseEnd.getDate() - 15);

      // Rango actual
      const from = formatDate(baseStart) + " 00:00:00";
      const to = formatDate(baseEnd) + " 23:59:59";

      // Rango 1 año atrás
      const from_1year =
        formatDate(
          new Date(
            baseStart.getFullYear() - 1,
            baseStart.getMonth(),
            baseStart.getDate()
          )
        ) + " 00:00:00";
      const to_1year =
        formatDate(
          new Date(
            baseEnd.getFullYear() - 1,
            baseEnd.getMonth(),
            baseEnd.getDate()
          )
        ) + " 23:59:59";

      try {
        // Las dos consultas en paralelo; en serie se sumaban los tiempos
        const [responseOld, responseCurrent] = await Promise.all([
          axios.get(`/map/orders?from=${from_1year}&to=${to_1year}`),
          axios.get(`/map/orders?from=${from}&to=${to}`),
        ]);
        this.ordersOlds = [...responseOld.data];
        const nuevasOrders = responseCurrent.data;

        this.pintarMarcadores(nuevasOrders);

        this.orders = [...nuevasOrders, ...this.ordersOlds];
        this.resumenOrders = [...this.orders];

        this.cargando = false;
      } catch (error) {
        console.error("Error cargando pedidos:", error);
      }
    },
    async cargarResumen() {
      this.cargandoResumen = true;

      const from = this.resumenFechaDesde + " 00:00:00";
      const to = this.resumenFechaHasta + " 23:59:59";

      // Calcular rango historico (1 año atras)
      const partsDesde = this.resumenFechaDesde.split('-');
      const partsHasta = this.resumenFechaHasta.split('-');
      const from1y = (parseInt(partsDesde[0]) - 1) + '-' + partsDesde[1] + '-' + partsDesde[2] + " 00:00:00";
      const to1y = (parseInt(partsHasta[0]) - 1) + '-' + partsHasta[1] + '-' + partsHasta[2] + " 23:59:59";

      try {
        const [response, responseOld] = await Promise.all([
          axios.get(`/map/orders?from=${from}&to=${to}`),
          axios.get(`/map/orders?from=${from1y}&to=${to1y}`)
        ]);

        this.resumenOrders = [...response.data, ...responseOld.data];
        this.resumenKey++;
        // El mapa muestra el rango filtrado; el anyo anterior solo sirve para comparar en los KPI
        this.pintarMarcadores(response.data);
      } catch (error) {
        console.error("Error cargando resumen:", error);
      } finally {
        this.cargandoResumen = false;
      }
    },
    // Sustituye los marcadores del mapa por los de estos pedidos.
    // /map/orders ya devuelve image y draft por line_item (enriquecer en el backend)
    pintarMarcadores(orders) {
      const marcadores = marcadoresPedidos(orders);

      // En bloque: addMarker uno a uno recalcula los clusteres en cada llamada
      this.markerClusterer.clearMarkers();
      this.markerClusterer.addMarkers(marcadores);
    },
    initMap() {
      const mapOptions = {
        //center: { lat: 40.7128, lng: -74.0060 }, // Coordenadas de ejemplo (Nueva York)
        center: { lat: 45, lng: -2 }, // Coordenadas de ejemplo (Alicante)
        zoom: 3,
      };

      // Crear el mapa
      this.map = new google.maps.Map(this.$refs.mapContainer, mapOptions);
      this.markerClusterer = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
        // El algoritmo por defecto agrupa y pinta el mundo entero en cada zoom, aunque quede fuera de pantalla.
        // Este solo trabaja con lo visible (mas un margen en px), a costa de reagrupar tambien al desplazarse.
        // Radio mayor y maxZoom alto: los grupos aguantan hasta muy cerca y hay menos que redibujar
        algorithm: new AgrupadoViewport({ maxZoom: 18, radius: 100, viewportPadding: 200 }),
        renderer: renderer,
      });
    },
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// ===== Design tokens (Data-Dense Dashboard) =====
$primary: #3B82F6;
$primary-soft: #60A5FA;
$cta: #F97316;
$bg: #F8FAFC;
$surface: #FFFFFF;
$border: #E2E8F0;
$text: #1E293B;
$text-mute: #64748B;
$text-soft: #94A3B8;

.home-v2 {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  color: $text;
  padding-bottom: 32px;

  // ===== HEADER =====
  .dash-header {
    background: $surface;
    border: 1px solid $border;
    border-radius: 14px;
    padding: 18px 20px 14px;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .dash-header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 18px;
    flex-wrap: wrap;
  }

  .dash-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $primary;
    letter-spacing: 0.14em;
    margin-bottom: 4px;

    &::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      background: $cta;
      border-radius: 50%;
      margin-right: 6px;
      vertical-align: middle;
      animation: home-pulse 2s infinite;
    }
  }

  .dash-title {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    color: $text;
    letter-spacing: -0.01em;

    .dash-title-tag {
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px;
      font-weight: 600;
      color: $cta;
      background: rgba(249, 115, 22, 0.12);
      padding: 3px 8px;
      border-radius: 4px;
      vertical-align: middle;
      margin-left: 6px;
    }
  }

  // ===== DATE RANGE =====
  .date-range {
    display: inline-flex;
    align-items: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  .date-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .date-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9.5px;
    font-weight: 600;
    color: $text-soft;
    letter-spacing: 0.1em;
    margin: 0 0 0 2px;
  }

  .date-input,
  input.date-input,
  .date-input.flatpickr-input,
  input.date-input.flatpickr-input,
  .date-input.form-control {
    display: block;
    height: 36px !important;
    width: 140px !important;
    padding: 0 12px !important;
    background: $bg !important;
    background-color: $bg !important;
    border: 1px solid $border !important;
    border-radius: 8px !important;
    font-size: 12px !important;
    font-family: 'JetBrains Mono', monospace !important;
    color: $text !important;
    transition: all 200ms ease;
    box-shadow: none !important;
    line-height: 34px !important;

    &::placeholder { color: $text-soft; }

    &:focus, &:focus-visible, &.active {
      outline: none !important;
      border-color: $primary !important;
      background: $surface !important;
      background-color: $surface !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12) !important;
    }
  }

  .btn-filter {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 16px;
    font-size: 12px;
    font-weight: 600;
    color: $surface;
    background: $primary;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 200ms ease;

    &:hover:not(:disabled) {
      background: darken($primary, 6%);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
    }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
    &:focus-visible { outline: 2px solid $primary; outline-offset: 2px; }
  }

  // ===== KPI STRIP =====
  .kpi-strip {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 480px) { grid-template-columns: 1fr; }
  }

  .kpi-card {
    background: $bg;
    border: 1px solid $border;
    border-radius: 10px;
    padding: 12px 14px;
    transition: all 200ms ease;

    &:hover {
      border-color: $primary-soft;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
    }
  }

  .kpi-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9.5px;
    font-weight: 600;
    color: $text-soft;
    letter-spacing: 0.12em;
    margin-bottom: 6px;
  }

  .kpi-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 26px;
    font-weight: 700;
    color: $text;
    line-height: 1.1;
    margin-bottom: 6px;
    font-feature-settings: "tnum", "zero";
  }

  .kpi-value-sm { font-size: 16px; }

  .kpi-foot {
    font-size: 10.5px;
    color: $text-mute;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }
  .dot-blue { background: $primary; }
  .dot-orange { background: $cta; }
  .dot-green { background: #10B981; animation: home-pulse 2s infinite; }

  // ===== MAP CARD =====
  .map-card {
    background: $surface;
    border: 1px solid $border;
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .map-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 18px;
    background: $bg;
    border-bottom: 1px solid $border;
  }

  .map-card-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 600;
    color: $text-mute;
    letter-spacing: 0.08em;
    text-transform: uppercase;

    svg { color: $primary; }
  }

  .map-legend {
    display: inline-flex;
    gap: 12px;
    font-size: 11px;
    color: $text-soft;
  }

  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .legend-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .legend-dot-current { background: $cta; }

  .map-container {
    height: 580px;
    width: 100%;
    transition: opacity 250ms ease;

    &.is-loading { opacity: 0.35; }
  }

  .map-loading {
    position: relative;
    height: 0;
  }

  .map-loading-inner {
    position: absolute;
    top: 240px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    background: $surface;
    border: 1px solid $border;
    border-radius: 12px;
    padding: 18px 28px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.1);
    text-align: center;

    p {
      margin: 8px 0 0;
      font-size: 12px;
      color: $text-mute;
    }
  }

  // ===== PANEL =====
  .panel {
    background: transparent;
  }

  // ===== ANIMATIONS =====
  @keyframes home-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.55; transform: scale(0.85); }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }
}

// ===== DARK MODE =====
.dark-layout .home-v2 {
  color: #d0d2d6;

  .dash-header,
  .map-card { background: #242424; border-color: #3b3b3b; }
  .dash-title { color: #e4e6eb; }

  .kpi-card {
    background: #1f1f1f;
    border-color: #3b3b3b;
    &:hover { border-color: $primary; }
  }
  .kpi-value { color: #e4e6eb; }
  .kpi-label, .date-label, .map-card-title { color: #6e7079; }

  .date-input,
  input.date-input,
  .date-input.flatpickr-input,
  .date-input.form-control {
    background: #1f1f1f !important;
    background-color: #1f1f1f !important;
    border-color: #3b3b3b !important;
    color: #d0d2d6 !important;
    &:focus { background: #2a2a2a !important; background-color: #2a2a2a !important; }
  }

  .map-card-header { background: #1f1f1f; border-bottom-color: #3b3b3b; }
  .map-loading-inner { background: #242424; border-color: #3b3b3b; }
}
</style>
